/**
 * `/results` — the companies that selection discovered (§10.2).
 */
import { useQuery } from '@tanstack/react-query'
import { SlidersHorizontal } from 'lucide-react'
import { Link, useSearch } from 'wouter'

import type { CompanyCard } from '@server/sql/companies.ts'
import { ApiError, api, keys } from '~/lib/api.ts'
import { useT } from '~/i18n/index.tsx'
import { count } from '~/i18n/format.ts'
import { Button, Skeleton } from '~/components/ui/primitives.tsx'
import { EmptyState, ErrorState } from '~/components/ui/empty.tsx'
import { Segmented } from '~/components/ui/tabs.tsx'
import { CompanyGridCard } from '~/features/companies/card.tsx'
import { parseSelection, selectionToSearch } from '~/features/filters/state.ts'
import { useSortParam } from '~/features/companies/sort.ts'
import { useDocumentTitle } from '~/lib/title.ts'

type Response = { companies: CompanyCard[]; total: number }

export function ResultsScreen() {
  const { t, tn, locale } = useT()
  useDocumentTitle(t('nav.results'))
  const search = useSearch()
  const { sort, setSort, SORT_OPTIONS } = useSortParam()

  // The sort lives in the same URL as the filter, so it is shared by a link
  // like everything else -- but it must not reach /api/filters, which
  // rejects unknown parameters.
  const selection = parseSelection(search)
  const filterQuery = selectionToSearch(selection)
  const query = `${filterQuery}${filterQuery ? '&' : '?'}sort=${sort}`

  const { data, error, refetch } = useQuery({
    queryKey: keys.companies(query),
    queryFn: () => api<Response>(`/api/companies${query}`),
    placeholderData: (prev) => prev,
  })

  const documents = data?.companies.reduce((n, c) => n + c.documents.total, 0) ?? 0

  return (
    <div className="mx-auto w-full max-w-[84rem] px-4 pb-10 pt-5 sm:px-5 2xl:max-w-[110rem]">
      {/* --- the filter summary, pinned, with a way back to it -------- */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-[var(--border)] pb-3">
        <div className="min-w-0">
          <h1 className="text-[length:var(--text-title)] font-semibold tracking-tight">
            {t('results.title')}
          </h1>
          {data && (
            <p className="tnum mt-0.5 text-[length:var(--text-data)] text-[var(--text-muted)]">
              {tn('common.companies', data.total, { n: count(data.total, locale) })}
              <span className="mx-1.5 text-[var(--border-strong)]">·</span>
              {tn('common.documents', documents, { n: count(documents, locale) })}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Segmented
            size="sm"
            ariaLabel={t('results.sort')}
            options={SORT_OPTIONS}
            value={sort}
            onChange={setSort}
          />
          <Link href={`/filter${filterQuery}`}>
            <Button size="sm">
              <SlidersHorizontal size={14} />
              {t('results.editFilter')}
            </Button>
          </Link>
        </div>
      </div>

      {error ? (
        <ErrorState
          title={t('common.error')}
          detail={(error as ApiError).message}
          requestId={(error as ApiError).requestId}
          onRetry={() => void refetch()}
          retryLabel={t('action.retry')}
        />
      ) : !data ? (
        <Grid>
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-52 w-full" />
          ))}
        </Grid>
      ) : data.companies.length === 0 ? (
        <EmptyState
          title={t('results.empty')}
          action={
            <Link href={`/filter${filterQuery}`}>
              <Button size="sm">{t('results.editFilter')}</Button>
            </Link>
          }
        >
          {t('results.emptyHint')}
        </EmptyState>
      ) : (
        <Grid>
          {data.companies.map((c) => (
            <CompanyGridCard key={c.symbol} c={c} />
          ))}
        </Grid>
      )}
    </div>
  )
}

/**
 * There was nothing between 640px and 1280px, so a landscape tablet showed
 * two very wide cards with slack either side. The card is a dossier -- more
 * of them visible at once is the whole value of this screen -- so the grid
 * now steps at every width where another column actually fits.
 */
function Grid({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {children}
    </div>
  )
}
